// src/Components/Activities/AddActivityForm.jsx
import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  Divider,
} from "@mui/material";
import { skillsOptions } from "./activityData";
import { useSnackbar } from "../Snackbar/SnackbarContext";
import DropDownField from "../../DropDownField";
import MultiDropDownField from "../../MultiDropDown";
import apiEndpoints from "../../apiconfig";

const baseForm = {
  branch_guid: "",
  class_guid: "",
  staff_guid: "",
  title: "",
  description: "",
  activity_date: "",
  images: "",
  skills: [],
};

const safeParseJSON = (val, fallback) => {
  try {
    if (typeof val === "string") {
      const trimmed = val.trim();
      if (!trimmed) return fallback;
      return JSON.parse(trimmed);
    }
    return Array.isArray(val) ? val : fallback;
  } catch {
    // If parse fails, maybe it's comma separated
    if (typeof val === "string") {
      return val
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    }
    return fallback;
  }
};

const AddActivityForm = ({
  mode = "add",
  initialData,
  onSubmit,
  onCancel,
  readOnly,
  loggedUser,
}) => {
  const [form, setForm] = useState(baseForm);
  const { showSnackbar } = useSnackbar();
  const [errors, setErrors] = useState({});
  const [branchList, setBranchList] = useState([]);
  const [classList, setClassList] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [isDropdownsLoaded, setIsDropdownsLoaded] = useState(false);

  // Debug useEffect to track values
  useEffect(() => {
    console.log("Current form values:", {
      branch_guid: form.branch_guid,
      class_guid: form.class_guid,
      staff_guid: form.staff_guid,
    });
    console.log("Dropdown lists:", {
      branchList,
      classList,
      staffList,
    });
  }, [form, branchList, classList, staffList]);

  // Debug useEffect to check branch matching
  useEffect(() => {
    if (form.branch_guid && branchList.length > 0) {
      const found = branchList.find(b => 
        String(b.guid).trim() === String(form.branch_guid).trim()
      );
      console.log('Branch match found:', found);
      console.log('Form branch_guid:', `"${form.branch_guid}"`);
      console.log('Available GUIDs:', branchList.map(b => `"${b.guid}"`));
    }
  }, [form.branch_guid, branchList]);

  // Debug useEffect to check initialData
  useEffect(() => {
    console.log('Initial Data:', initialData);
    console.log('Initial branch_guid:', initialData?.branch_guid);
  }, [initialData]);

  // Normalizer: return object with { guid, name }
  const normalize = (item) => ({
    guid: item.guid,
    name:
      item.name || item.branch_name || item.class_name || item.staff_name || "",
  });

  // Fetch branches (normalized)
  const fetchBranches = async () => {
    const token = localStorage.getItem("token");
    try {
      const res = await fetch(`${apiEndpoints.dropdown}?type=branch`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.status === "success" && Array.isArray(data.data)) {
        const normalized = data.data.map(normalize);
        setBranchList(normalized);
        return normalized;
      }
    } catch (e) {
      console.error("fetchBranches error", e);
    }
    return [];
  };

  // Fetch classes for a branch (normalized)
  const fetchClasses = async (branchGuid) => {
    if (!branchGuid) return [];
    const token = localStorage.getItem("token");
    try {
      const res = await fetch(
        `${apiEndpoints.dropdown}?type=class&branch_guid=${branchGuid}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      if (data.status === "success" && Array.isArray(data.data)) {
        const normalized = data.data.map(normalize);
        setClassList(normalized);
        return normalized;
      }
    } catch (e) {
      console.error("fetchClasses error", e);
    }
    return [];
  };

  // Fetch staff for a branch (normalized)
  const fetchStaff = async (branchGuid) => {
    if (!branchGuid) return [];
    if (loggedUser.role === "staff") return [];
    const token = localStorage.getItem("token");
    try {
      const res = await fetch(
        `${apiEndpoints.dropdown}?type=staff&branch_guid=${branchGuid}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      if (data.status === "success" && Array.isArray(data.data)) {
        const normalized = data.data.map(normalize);
        setStaffList(normalized);
        return normalized;
      }
    } catch (e) {
      console.error("fetchStaff error", e);
    }
    return [];
  };

  // Ensure list contains an option for given guid; if not, inject fallback
  const ensureOptionInList = (list, guid, fallbackName) => {
    if (!guid) return list;
    const found = list.find((i) => String(i.guid) === String(guid));
    if (found) return list;
    // inject at start so it shows
    return [{ guid, name: fallbackName || "Unknown" }, ...list];
  };

  // Enhanced ensureOptionInList with better debugging
  const ensureOptionInListEnhanced = (list, guid, fallbackName, listName = "list") => {
    if (!guid) {
      console.log(`${listName}: No GUID provided`);
      return list;
    }
    
    const found = list.find((i) => String(i.guid) === String(guid));
    
    if (found) {
      console.log(`${listName}: Found match for GUID ${guid}`);
      return list;
    }
    
    console.log(`${listName}: No match found for GUID ${guid}, adding fallback`);
    console.log(`${listName} items:`, list.map(item => ({guid: item.guid, name: item.name})));
    
    // inject at start so it shows
    return [{ guid, name: fallbackName || "Unknown" }, ...list];
  };

  // Load data for both create & edit, hydrate form after lists ready
  useEffect(() => {
    let mounted = true;

    const load = async () => {
      console.log('load function called, mode:', mode, 'initialData:', initialData);
      setIsDropdownsLoaded(false);

      // always fetch branches first (so branchList exists)
      const branches = await fetchBranches();
      console.log('Fetched branches:', branches);

      if (!initialData) {
        // CREATE mode: auto-select branch for staff role
        console.log('CREATE mode');
        if (loggedUser.role === "staff" && branches.length > 0) {
          const staffBranch = branches[0].guid;
          console.log('Staff branch selected:', staffBranch);
          setForm((f) => ({
            ...baseForm,
            branch_guid: staffBranch,
            staff_guid: loggedUser.id,
          }));
          await fetchClasses(staffBranch);
          await fetchStaff(staffBranch);
        }
        if (mounted) setIsDropdownsLoaded(true);
        return;
      }

      // EDIT mode:
      console.log('EDIT mode, initialData:', initialData);
      console.log('initialData.branch_guid:', initialData.branch_guid);
      console.log('initialData.branch_name:', initialData.branch_name);
      
      // fetch classes & staff for the existing branch guid
      const branchGuid = initialData.branch_guid;
      const classes = await fetchClasses(branchGuid);
      const staffs = await fetchStaff(branchGuid);

      console.log('Fetched classes for branch', branchGuid, ':', classes);
      console.log('Fetched staff for branch', branchGuid, ':', staffs);

      // If branch/class/staff lists do not include the GUID from initialData,
      // inject fallback entries using branch_name, class_name, staff_name fields
      const branchNameFallback = initialData.branch_name || "";
      const classNameFallback = initialData.class_name || "";
      const staffNameFallback = initialData.staff_name || "";

      console.log('Fallback names:', {
        branchNameFallback,
        classNameFallback,
        staffNameFallback
      });

      const finalBranchList = ensureOptionInListEnhanced(
        branches,
        initialData.branch_guid,
        branchNameFallback,
        'branchList'
      );
      const finalClassList = ensureOptionInListEnhanced(
        classes,
        initialData.class_guid,
        classNameFallback,
        'classList'
      );
      const finalStaffList = ensureOptionInListEnhanced(
        staffs,
        initialData.staff_guid,
        staffNameFallback,
        'staffList'
      );

      console.log('Final lists after ensureOptionInList:', {
        finalBranchList,
        finalClassList,
        finalStaffList
      });

      // update lists to include fallbacks
      if (mounted) {
        setBranchList(finalBranchList);
        setClassList(finalClassList);
        setStaffList(finalStaffList);
      }

      // parse skills and images safely
      const skillsParsed = safeParseJSON(initialData.skills, []);
      const imagesParsed = Array.isArray(initialData.photos)
        ? initialData.photos
        : [];


      // hydrate the form AFTER lists are ready
      if (mounted) {
        console.log('Setting form with initialData values:', {
          branch_guid: initialData.branch_guid,
          class_guid: initialData.class_guid,
          staff_guid: initialData.staff_guid
        });
        
        const newForm = {
          ...baseForm,
          ...initialData,
          branch_guid: initialData.branch_guid || "",
          class_guid: initialData.class_guid || "",
          staff_guid: initialData.staff_guid || "",
          skills: Array.isArray(skillsParsed)
            ? skillsParsed
            : Array.isArray(initialData.skills)
            ? initialData.skills
            : [],
          images: imagesParsed.join("\n"),
          activity_date: initialData.activity_date || initialData.date || "",
          title: initialData.title || "",
          description: initialData.description || "",
        };
        
        console.log('New form to be set:', newForm);
        setForm(newForm);
        setIsDropdownsLoaded(true);
        console.log('Form set and dropdowns loaded');
      }
    };

    load();

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData]);

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = () => {
    const newErrors = {
      title: !form.title,
      description: !form.description,
      activity_date: !form.activity_date,
      staff_guid: !form.staff_guid,
      branch_guid: !form.branch_guid,
      class_guid: !form.class_guid,
    };

    setErrors(newErrors);

    if (Object.values(newErrors).some(Boolean)) {
      showSnackbar("Please fill all required fields", "error");
      return;
    }

    const images = (form.images || "")
      .split("\n")
      .map((u) => u.trim())
      .filter(Boolean);

    const payload = {
      ...initialData,
      ...form,
      staff_guid: loggedUser.role === "staff" ? loggedUser.id : form.staff_guid,
      images, // ✅ ARRAY
      skills: form.skills,
      activity_date: form.activity_date,
    };

    onSubmit?.(payload);
    showSnackbar(
      mode === "add"
        ? "Activity created successfully!"
        : "Activity updated successfully!",
      "success"
    );
  };

  if (!isDropdownsLoaded) {
    return <div>Loading…</div>;
  }

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
        🎨 {mode === "add" ? "Add New Activity" : "Edit Activity"}
      </Typography>

      <Typography variant="body2" sx={{ color: "text.secondary", mb: 3 }}>
        Fill in the activity details below. Required fields are marked with *.
      </Typography>

      <Divider sx={{ mb: 3 }} />

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <TextField
            label="Activity Title *"
            fullWidth
             disabled={readOnly}
            value={form.title || ""}
            error={!!errors.title}
            onChange={(e) => handleChange("title", e.target.value)}
            sx={{ mb: 3 }}
            InputProps={{ sx: { borderRadius: 3 } }}
          />

          <DropDownField
            label="Branch *"
            value={form.branch_guid}
             disabled={readOnly}
            options={(() => {
              // Ensure current value is in the options list
              const currentGuid = form.branch_guid;
              const inList = branchList.some(
                (b) => String(b.guid) === String(currentGuid)
              );

              if (currentGuid && !inList && initialData?.branch_name) {
                console.log("Adding fallback branch to options:", {
                  guid: currentGuid,
                  name: initialData.branch_name,
                });
                return [
                  {
                    guid: currentGuid,
                    name: `${initialData.branch_name} (current)`,
                  },
                  ...branchList,
                ];
              }
              return branchList;
            })()}
            error={!!errors.branch_guid}
            onChange={async (guid) => {
              console.log("Branch changed to:", guid);
              handleChange("branch_guid", guid);
              handleChange("class_guid", "");
              handleChange("staff_guid", "");
              const classes = await fetchClasses(guid);
              const staffs = await fetchStaff(guid);
              // ensure lists updated
            }}
          />

          <DropDownField
            label="Class *"
            value={form.class_guid}
             disabled={readOnly}
            options={(() => {
              // Ensure current value is in the options list
              const currentGuid = form.class_guid;
              const inList = classList.some(
                (c) => String(c.guid) === String(currentGuid)
              );

              if (currentGuid && !inList && initialData?.class_name) {
                console.log("Adding fallback class to options:", {
                  guid: currentGuid,
                  name: initialData.class_name,
                });
                return [
                  {
                    guid: currentGuid,
                    name: `${initialData.class_name} (current)`,
                  },
                  ...classList,
                ];
              }
              return classList;
            })()}
            error={!!errors.class_guid}
            onChange={(guid) => handleChange("class_guid", guid)}
          />

          {loggedUser.role === "admin" ? (
            <DropDownField
              label="Staff *"
               disabled={readOnly}
              value={form.staff_guid}
              options={(() => {
                // Ensure current value is in the options list
                const currentGuid = form.staff_guid;
                const inList = staffList.some(
                  (s) => String(s.guid) === String(currentGuid)
                );

                if (currentGuid && !inList && initialData?.staff_name) {
                  console.log("Adding fallback staff to options:", {
                    guid: currentGuid,
                    name: initialData.staff_name,
                  });
                  return [
                    {
                      guid: currentGuid,
                      name: `${initialData.staff_name} (current)`,
                    },
                    ...staffList,
                  ];
                }
                return staffList;
              })()}
              error={!!errors.staff_guid}
              onChange={(guid) => handleChange("staff_guid", guid)}
            />
          ) : (
            <TextField
              label="Staff"
              fullWidth
                disabled={readOnly}
              value={loggedUser.name}
              sx={{ mb: 3 }}
              helperText="Auto-assigned to logged-in staff"
            />
          )}

          <MultiDropDownField
            label="Skills Developed"
            value={Array.isArray(form.skills) ? form.skills : []}
            options={skillsOptions}
            onChange={(selected) => handleChange("skills", selected)}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            label="Description *"
            fullWidth
            multiline
              disabled={readOnly}

            minRows={3}
            value={form.description || ""}
            error={!!errors.description}
            onChange={(e) => handleChange("description", e.target.value)}
            sx={{ mb: 3 }}
            InputProps={{ sx: { borderRadius: 3 } }}
          />

          <TextField
            label="Activity Date *"
            type="date"
            fullWidth
             disabled={readOnly}
            value={form.activity_date || ""}
            error={!!errors.activity_date}
            onChange={(e) => handleChange("activity_date", e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 3 }}
            InputProps={{ sx: { borderRadius: 3 } }}
          />

          <TextField
            label="Image URLs (one per line)"
            fullWidth
            multiline
             disabled={readOnly}
            minRows={3}
            value={form.images || ""}
            onChange={(e) => handleChange("images", e.target.value)}
            sx={{ mb: 3 }}
            helperText="Paste image URLs; each line will be treated as one image."
            InputProps={{ sx: { borderRadius: 3 } }}
          />
        </Grid>
      </Grid>

      <Box sx={{ mt: 4, display: "flex", justifyContent: "end", gap: 2 }}>
        <Button
          variant="outlined"
          onClick={onCancel}
          sx={{ mb: 2, borderRadius: 999, px: 3 }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          sx={{ mb: 2, borderRadius: 999, px: 3 }}
        >
          {mode === "add" ? "Create Activity" : "Update Activity"}
        </Button>
      </Box>
    </Box>
  );
};

export default AddActivityForm;